'use client';

import { useEffect, useMemo } from 'react';
import useMarketsForDlobToTrack, {
	MarketStatePriority,
} from '../useMarketsForDlobToTrack';
import useVelocityStore from '../../stores/VelocityStore/useVelocityStore';
import { useUpdateAccountLoaderCadence } from '../velocity-actions/connection/useUpdateAccountLoaderCadence';
import { MarketId } from '@velocity-exchange/common';
import {
	PerpMarketAccount,
	SpotMarketAccount,
	CustomizedCadenceBulkAccountLoader,
} from '@velocity-exchange/sdk';
import Env from '../../environmentVariables/EnvironmentVariables';

const SELECTED_MARKET_CADENCE = Env.selectedMarketPollingCadenceMs ?? 1000;
const ACTIVE_MARKET_CADENCE = Env.activeMarketPollingCadenceMs ?? 1000;
const BACKGROUND_MARKET_CADENCE =
	Env.backgroundMarketPollingCadenceMs ?? 60_000;

const useSyncAccountLoaderCadenceWithMarketSubs = () => {
	const marketsToTrack = useMarketsForDlobToTrack();
	const velocityClient = useVelocityStore((s) => s.velocityClient.client);
	const accountLoader = useVelocityStore((s) => s.connection.accountLoader);
	const { updateAccountLoaderCadence } = useUpdateAccountLoaderCadence();

	const updateCadenceForMarket = (marketId: MarketId, cadence: number) => {
		if (!velocityClient?.isSubscribed) {
			return;
		}

		const marketAccount = marketId.isPerp
			? velocityClient?.getPerpMarketAccount(marketId.marketIndex)
			: velocityClient?.getSpotMarketAccount(marketId.marketIndex);

		if (
			!marketAccount ||
			!accountLoader ||
			!(accountLoader instanceof CustomizedCadenceBulkAccountLoader)
		)
			return;

		// Update market account cadence
		const currentMarketCadence = accountLoader.getAccountCadence(
			marketAccount.pubkey
		);

		if (currentMarketCadence !== cadence) {
			updateAccountLoaderCadence(marketAccount.pubkey, cadence);
		}

		// Update oracle account cadence
		const oraclePublicKey = (() => {
			if (marketId.isPerp) {
				return (marketAccount as PerpMarketAccount).oracle;
			}

			return (marketAccount as SpotMarketAccount).oracle;
		})();

		const currentOracleCadence =
			accountLoader.getAccountCadence(oraclePublicKey);

		if (currentOracleCadence !== cadence) {
			updateAccountLoaderCadence(oraclePublicKey, cadence);
		}
	};

	// Get markets from each category
	const selectedMarkets = useMemo(
		() => marketsToTrack.categories[MarketStatePriority.SelectedMarket] || [],
		[marketsToTrack.categories[MarketStatePriority.SelectedMarket]]
	);
	const deepMarkets = useMemo(
		() => marketsToTrack.categories[MarketStatePriority.BackgroundDeep] || [],
		[marketsToTrack.categories[MarketStatePriority.BackgroundDeep]]
	);
	const shallowMarkets = useMemo(
		() =>
			marketsToTrack.categories[MarketStatePriority.BackgroundShallow] || [],
		[marketsToTrack.categories[MarketStatePriority.BackgroundShallow]]
	);

	// Handle selected markets
	useEffect(() => {
		if (!velocityClient || !accountLoader) return;

		selectedMarkets.forEach((marketId) => {
			updateCadenceForMarket(marketId, SELECTED_MARKET_CADENCE);
		});
	}, [velocityClient, accountLoader, selectedMarkets]);

	// Handle deep markets
	useEffect(() => {
		if (!velocityClient || !accountLoader) return;

		deepMarkets.forEach((marketId) => {
			updateCadenceForMarket(marketId, ACTIVE_MARKET_CADENCE);
		});
	}, [velocityClient, accountLoader, deepMarkets]);

	// Handle shallow markets
	useEffect(() => {
		if (!velocityClient || !accountLoader) return;

		shallowMarkets.forEach((marketId) => {
			updateCadenceForMarket(marketId, BACKGROUND_MARKET_CADENCE);
		});
	}, [velocityClient, accountLoader, shallowMarkets]);
};

export default useSyncAccountLoaderCadenceWithMarketSubs;
